import {useEffect, useState} from 'react'
import {useFormik} from 'formik'
import useSetting from '../../hooks/useSetting'
import uploadFileSecurely from '../../helper/secureUpload'
import HomeBannerCard from '../_shared/HomeBannerCard'

// Lets Errin's team change the mobile app's login/sign-up logo and welcome
// copy without a developer + app store release, every time - flagged
// 2026-09-17 in triage: "Logo and sign-in screen content aren't editable
// by your team." Mirrors the existing PrivacyPolicy.jsx page's structure
// (same useSetting hook, same submit/MessageModal pattern). Logo uploads
// go through uploadFileSecurely (a presigned S3 URL from the backend) -
// see that file for why, replacing two now-deleted helpers that held a
// live AWS key in plaintext.
const Branding = () => {
  const [error, setError] = useState(false)
  const [showModal, setShowModal] = useState(false)
  const [message, setMessage] = useState('')
  const [isLoading, setLoading] = useState(false)
  const [settings, setSettings] = useState({})

  // Local-only state for the two logo inputs: the file the person just
  // picked (not yet uploaded) plus a preview URL for it. Falls back to
  // showing the currently-saved logoUrl/logoUrlEs when nothing's picked.
  const [logoFile, setLogoFile] = useState(null)
  const [logoPreview, setLogoPreview] = useState(null)
  const [logoEsFile, setLogoEsFile] = useState(null)
  const [logoEsPreview, setLogoEsPreview] = useState(null)

  const {getSettings, updateSettings} = useSetting()

  useEffect(() => {
    const init = async () => {
      setLoading(true)
      try {
        const {data} = await getSettings()
        if (data) {
          setSettings(data)
        }
      } catch (err) {
        setError(true)
      } finally {
        setLoading(false)
      }
    }
    init()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      signInWelcomeText: settings?.signInWelcomeText || '',
      signInWelcomeTextEs: settings?.signInWelcomeTextEs || '',
      signUpWelcomeText: settings?.signUpWelcomeText || '',
      signUpWelcomeTextEs: settings?.signUpWelcomeTextEs || '',
    },
    onSubmit: async (values) => {
      setLoading(true)
      try {
        // Upload any newly-picked logo files first, so we have real URLs
        // to save. Keep the existing URL for whichever one wasn't changed.
        let logoUrl = settings?.logoUrl || ''
        let logoUrlEs = settings?.logoUrlEs || ''

        if (logoFile) {
          const {fileUrl} = await uploadFileSecurely(logoFile)
          logoUrl = fileUrl
        }
        if (logoEsFile) {
          const {fileUrl} = await uploadFileSecurely(logoEsFile)
          logoUrlEs = fileUrl
        }

        await updateSettings({logoUrl, logoUrlEs}, 'logo')
        await updateSettings(
          {
            signInWelcomeText: values.signInWelcomeText,
            signInWelcomeTextEs: values.signInWelcomeTextEs,
          },
          'signInWelcomeText'
        )
        await updateSettings(
          {
            signUpWelcomeText: values.signUpWelcomeText,
            signUpWelcomeTextEs: values.signUpWelcomeTextEs,
          },
          'signUpWelcomeText'
        )

        setSettings((prev) => ({...prev, logoUrl, logoUrlEs, ...values}))
        setLogoFile(null)
        setLogoPreview(null)
        setLogoEsFile(null)
        setLogoEsPreview(null)
        setMessage('Branding updated successfully')
      } catch (err) {
        setError(true)
        setMessage(err?.message || 'Something went wrong')
      } finally {
        setLoading(false)
        setShowModal(true)
      }
    },
  })

  const handleLogoChange = (e, setFile, setPreview) => {
    const file = e.target.files?.[0]
    if (!file) return
    setFile(file)
    setPreview(URL.createObjectURL(file))
  }

  const LogoField = ({label, currentUrl, preview, onChange}) => (
    <div className='row mb-6'>
      <label className='col-lg-4 col-form-label fw-bold fs-6'>{label}</label>
      <div className='col-lg-8 fv-row d-flex align-items-center'>
        <img
          src={preview || currentUrl || '/media/svg/avatars/blank.svg'}
          alt={label}
          style={{
            width: 80,
            height: 80,
            objectFit: 'contain',
            border: '1px solid #e4e6ef',
            borderRadius: 8,
            marginRight: 16,
            background: '#fff',
          }}
        />
        <input
          type='file'
          accept='.png,.jpg,.jpeg'
          className='form-control form-control-lg form-control-solid'
          onChange={onChange}
        />
      </div>
    </div>
  )

  return (
    <>
      <HomeBannerCard
        settingKey='onlineBannerUrl'
        endpoint='/admin/online-banner'
        bannerName='Online Sessions'
        title='Home screen banner: Online Sessions'
        description="The photo on the Online Sessions banner on the app's Home screen (between Retreats & Experiences and Guided Meditations)."
      />
      <div className='card mb-5 mb-xl-10'>
        <div className='card-header border-0'>
          <div className='card-title m-0'>
            <h3 className='fw-bolder m-0'>Logo &amp; Sign-In Content</h3>
          </div>
        </div>
        <div className='card-body border-top p-9'>
          <p className='text-muted'>
            Changes here go live in the app without needing a new app store release. Leave a
            field blank to keep the app's default.
          </p>
        </div>
        <form onSubmit={formik.handleSubmit} noValidate className='form'>
          <div className='card-body border-top p-9'>
            <h4 className='mb-5'>Logo</h4>
            <LogoField
              label='Logo (English)'
              currentUrl={settings?.logoUrl}
              preview={logoPreview}
              onChange={(e) => handleLogoChange(e, setLogoFile, setLogoPreview)}
            />
            <LogoField
              label='Logo (Spanish)'
              currentUrl={settings?.logoUrlEs}
              preview={logoEsPreview}
              onChange={(e) => handleLogoChange(e, setLogoEsFile, setLogoEsPreview)}
            />

            <h4 className='mb-5 mt-10'>Sign-In Welcome Text</h4>
            <div className='row mb-6'>
              <label className='col-lg-4 col-form-label fw-bold fs-6'>English</label>
              <div className='col-lg-8 fv-row'>
                <input
                  type='text'
                  className='form-control form-control-lg form-control-solid'
                  placeholder='Hello there, Log in to continue'
                  {...formik.getFieldProps('signInWelcomeText')}
                />
              </div>
            </div>
            <div className='row mb-6'>
              <label className='col-lg-4 col-form-label fw-bold fs-6'>Spanish</label>
              <div className='col-lg-8 fv-row'>
                <input
                  type='text'
                  className='form-control form-control-lg form-control-solid'
                  placeholder='Hola, Inicia sesión para continuar'
                  {...formik.getFieldProps('signInWelcomeTextEs')}
                />
              </div>
            </div>

            <h4 className='mb-5 mt-10'>Sign-Up Welcome Text</h4>
            <div className='row mb-6'>
              <label className='col-lg-4 col-form-label fw-bold fs-6'>English</label>
              <div className='col-lg-8 fv-row'>
                <input
                  type='text'
                  className='form-control form-control-lg form-control-solid'
                  placeholder='Hello there, Sign up to continue'
                  {...formik.getFieldProps('signUpWelcomeText')}
                />
              </div>
            </div>
            <div className='row mb-6'>
              <label className='col-lg-4 col-form-label fw-bold fs-6'>Spanish</label>
              <div className='col-lg-8 fv-row'>
                <input
                  type='text'
                  className='form-control form-control-lg form-control-solid'
                  placeholder='Hola, Regístrate para continuar'
                  {...formik.getFieldProps('signUpWelcomeTextEs')}
                />
              </div>
            </div>
          </div>

          <div className='card-footer d-flex justify-content-end py-6 px-9'>
            <button type='submit' className='btn btn-primary' disabled={isLoading}>
              {!isLoading && 'Submit'}
              {isLoading && (
                <span className='indicator-progress' style={{display: 'block'}}>
                  Please wait...{' '}
                  <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                </span>
              )}
            </button>
            {showModal && (
              <MessageModal
                setShowModal={setShowModal}
                message={message}
                error={error}
                setError={setError}
              />
            )}
          </div>
        </form>
      </div>
    </>
  )
}

const MessageModal = ({setShowModal, message, error, setError}) => {
  return (
    <>
      <div
        className='modal fade show d-block'
        id='kt_modal_branding_settings'
        role='dialog'
        tabIndex={-1}
        aria-modal='true'
      >
        <div className='modal-dialog modal-dialog-centered mw-550px'>
          <div className='modal-content'>
            <div className='modal-body scroll-y'>
              <div className='modal-header'>
                <h5 className='modal-title fs-2'>{error ? 'Error' : 'Success'}</h5>
              </div>
              <div className='modal-body fs-3'>
                <p>{message}</p>
              </div>
              <div className='modal-footer'>
                <button
                  onClick={() => {
                    setShowModal(false)
                    setError(false)
                  }}
                  type='button'
                  className='btn btn-primary'
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className='modal-backdrop fade show'></div>
    </>
  )
}

export default Branding
